import React, { useState } from "react";
import toast from "react-hot-toast";
import moment from "moment";
import { CardComp, ButtonComp, TableCompData } from "../../../components";
import { formatMoney } from "../../../utils";
import { describeLateFeeRule } from "../../../components/Loan/lateFee";
import { useWaiveLoanLateFeeMutation } from "../../../store/admin/loanRepayment";

const STATUS_COLOR = { PENDING: "#FFA500", PAID: "#19BC31", WAIVED: "#A1A0A0" };

/** Late fees charged on a loan, with a waive action for staff. Renders nothing for a loan without any. */
export default function LoanLateFees({ rule, fees = [], balance = 0 }) {
  const [waiving, setWaiving] = useState(null);
  const [reason, setReason] = useState("");
  const [waive, { isLoading }] = useWaiveLoanLateFeeMutation();

  if (!rule && !fees.length) return null;

  const confirmWaive = async () => {
    const response = await waive({ feeId: waiving.id, reason: reason.trim() || undefined });
    if (response.error) {
      return toast.error(response.error?.data?.message || "Could not waive this fee");
    }
    toast.success("Late fee waived");
    setWaiving(null);
    setReason("");
  };

  const columns = [
    {
      name: "CHARGED",
      selector: (row) => moment(row.charged_on).format("DD-MMM-YYYY"),
      minWidth: "130px",
    },
    {
      name: "ON OVERDUE OF",
      selector: (row) => formatMoney(parseFloat(row.base_amount || 0)),
      minWidth: "140px",
    },
    {
      name: "FEE",
      selector: (row) => <span className="fw-2">{formatMoney(parseFloat(row.amount || 0))}</span>,
      minWidth: "120px",
    },
    {
      name: "PAID",
      selector: (row) => formatMoney(parseFloat(row.amount_paid || 0)),
      minWidth: "120px",
    },
    {
      name: "STATUS",
      selector: (row) => (
        <div
          className="px-3 py-1 rounded-pill text-white text-center"
          style={{ backgroundColor: STATUS_COLOR[row.status] || "#A1A0A0", fontSize: "12px", minWidth: "90px" }}
        >
          {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
        </div>
      ),
      minWidth: "120px",
    },
    {
      name: "ACTION",
      cell: (row) =>
        row.status === "PENDING" ? (
          <ButtonComp
            onClick={() => setWaiving(row)}
            btnText="Waive"
            btnClassName="bg-white border text-danger px-3 fw-2 py-1 rounded"
          />
        ) : (
          <span className="text-muted small" title={row.waive_reason || ""}>
            {row.status === "WAIVED" ? "Waived" : "-"}
          </span>
        ),
      minWidth: "110px",
    },
  ];

  return (
    <CardComp cardClassName="border-0 shadow-sm">
      <div className="mb-3">
        <h5 className="fw-bold mb-1 text-primary1">Late Fees</h5>
        <p className="text-muted mb-0 small">
          {rule ? describeLateFeeRule(rule) : "No late fee rule is set for this loan type now"}
        </p>
        {balance > 0 && (
          <p className="text-danger fw-semibold mt-2 mb-0">Unpaid late fees: {formatMoney(balance)}</p>
        )}
      </div>

      {fees.length > 0 && <TableCompData marginBottom="1rem" columns={columns} data={fees} />}

      {waiving && (
        <div className="border rounded p-3 mt-2">
          <p className="mb-2">
            Waive the remaining <strong>{formatMoney(parseFloat(waiving.balance || 0))}</strong> of this late fee?
            Anything already paid stays paid.
          </p>
          <input
            type="text"
            className="form-control mb-3"
            placeholder="Reason (optional)"
            maxLength={255}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="d-flex gap-2 justify-content-end">
            <ButtonComp
              onClick={() => {
                setWaiving(null);
                setReason("");
              }}
              btnText="Cancel"
              btnClassName="bg-white border px-3 fw-2 py-2 rounded"
            />
            <ButtonComp
              onClick={confirmWaive}
              btnText={isLoading ? "Waiving..." : "Waive fee"}
              disabled={isLoading}
              btnClassName="bg-primary1 text-white px-3 fw-2 py-2 rounded border-0"
            />
          </div>
        </div>
      )}
    </CardComp>
  );
}
